import { useCallback, useMemo } from 'react';
import {
  Control,
  UseFieldArrayReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { v4 as uuidV4 } from 'uuid';
import * as yup from 'yup';

import { useUpdateUserSettings } from '~/components/UserSettings';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { ConfigureQuickLinksDialogContentProps } from '~/scenes/MyWorkspace/components/Widgets/components/QuickLinksWidget/components/ConfigureQuickLinksDialogContent/component';

import { QuickLinkViewItem, QuickLinksFormValues } from '../../models';
import { QuickLinksUserSettingsKey } from '../../userSettings';

export type QuickLinksFieldArray = UseFieldArrayReturn<
  QuickLinksFormValues,
  'quickLinks'
>;

export interface UseConfigureQuickLinksDialogContentOutput {
  handleSave: (
    e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => void;
  addNewLink: () => void;
  control: Control<QuickLinksFormValues>;
  quickLinksFieldArray: QuickLinksFieldArray;
}

export const useConfigureQuickLinksDialogContent = (
  props: ConfigureQuickLinksDialogContentProps,
): UseConfigureQuickLinksDialogContentOutput => {
  const { quickLinkItems, onClose } = props;

  const validationSchema = useMemo(
    () =>
      yup.object().shape<QuickLinksFormValues>({
        quickLinks: yup
          .array()
          .of(
            yup
              .object()
              .shape<QuickLinkViewItem>({
                id: yup.string(),
                name: yup.string().required(),
                description: yup.string(),
                url: yup
                  .string()
                  .required()
                  .test(
                    'url',
                    // currently not shown in the ui
                    'it needs to be a full url (starts with "http(s)://") or a relative one (starts with "/")',
                    (value) =>
                      !value ||
                      value.startsWith('/') ||
                      value.startsWith('http://') ||
                      value.startsWith('https://'),
                  ),
                active: yup.boolean(),
              })
              .required(),
          )
          .required(),
      }),
    [],
  );
  const resolver = useYupValidationResolver(validationSchema);

  const { control, handleSubmit } = useForm<QuickLinksFormValues>({
    defaultValues: {
      quickLinks: quickLinkItems,
    },
    resolver,
    reValidateMode: 'onSubmit',
  });

  // Add new link
  const quickLinksFieldArray: QuickLinksFieldArray = useFieldArray({
    control,
    name: 'quickLinks',
  });
  const { append } = quickLinksFieldArray;

  const addNewLink = useCallback(() => {
    const newLink = {
      id: uuidV4(),
      name: '',
      description: '',
      url: '',
      active: true,
    } as QuickLinkViewItem;
    append(newLink);
  }, [append]);

  // Saving links
  const updateUserSettings = useUpdateUserSettings();

  const handleSave = handleSubmit((data) => {
    const formQuickLinks = data.quickLinks || [];

    const hiddenLinks = formQuickLinks
      .filter((l) => !l.active)
      .map((l) => l.id);

    const linksOrder = formQuickLinks.map((l) => l.id);

    const customLinks = formQuickLinks
      .filter((l) => !l.location)
      .map((link) => ({
        name: link.name,
        url: link.url,
        id: link.id,
      }));

    updateUserSettings({
      [QuickLinksUserSettingsKey]: {
        hiddenLinks,
        customLinks,
        linksOrder,
      },
    });
    onClose();
  });

  return {
    handleSave,
    addNewLink,
    control,
    quickLinksFieldArray,
  };
};
